import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { CalendarEventModel } from '../../../../../../types';
import { moveCalendarEvent, UserKeys } from '../../../../../api/users-api';
import { useAnalytics } from '../../../../../shared/analytics/create-events';
import ChangeLogDialog from '../../../../../shared/change-log-dialog';
import SharedDialog from '../../../../../shared/shared-dialog';
import { handleErrors } from '../../../../../utils/errors';
import { useStore } from '../../../../state/zustand-store';
import * as analytics from '../../../../../shared/analytics/analytics.json';
import SharedAutocomplete from '../../../../../shared/shared-autocomplete';
import { ModuleList } from '../../../../../shared/analytics/module-list';
import {
  CalendarRoles,
  isAbleToSeeUIElement,
} from '../../../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

interface CalendarEventsMoveDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  selectedEvent: CalendarEventModel;
  changeLogRoles: string[];
  calendarId: string;
  isAllUsers: boolean;
  dateRange: {
    start: number;
    end: number;
  };
}

export default function CalendarEventsMoveDialog({
  open,
  handleClose,
  userEmail,
  calendarId,
  selectedEvent,
  changeLogRoles,
  isAllUsers,
  dateRange,
}: CalendarEventsMoveDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const [autoCompleteValue, setAutoCompleteValue] = useState('');

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      moveCalendarEvent(
        userEmail,
        calendarId,
        selectedEvent.recurringEventId
          ? selectedEvent.recurringEventId
          : selectedEvent.id,
        autoCompleteValue,
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Calendar event moved successfully!',
      });

      queryClient.setQueriesData(
        {
          queryKey: [
            UserKeys.CalendarEvents,
            calendarId,
            userEmail,
            {
              start: dateRange.start,
              end: dateRange.end,
            },
          ],
        },
        (oldData: CalendarEventModel[] | undefined) => {
          if (oldData) {
            if (selectedEvent.recurringEventId) {
              return oldData.filter(
                (event) =>
                  event.recurringEventId !== selectedEvent.recurringEventId
              );
            } else {
              return oldData.filter((event) => event.id !== selectedEvent.id);
            }
          } else {
            return [];
          }
        }
      );

      setAutoCompleteValue('');
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue moving this calendar event. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        isAllUsers
          ? analytics.calendars.calendars.move_calendar_event
          : analytics.user_management.user_details.calendars
              .move_calendar_event,
        isAllUsers
          ? {
              module: ModuleList.modules.calendars.name,
              section: ModuleList.modules.calendars.sections.calendars,
            }
          : {
              module: ModuleList.modules.userManagement.name,
              section: ModuleList.modules.userManagement.sections.calendars,
            }
      );
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (isAbleToSeeUIElement(changeLogRoles, CalendarRoles.MoveEvents.value)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={
        selectedEvent.recurringEventId
          ? 'Move Recurring Event'
          : 'Move Calendar Event'
      }
      dialogContent={
        <Stack gap={2}>
          <Typography
            sx={{
              mt: 1,
            }}
          >
            {selectedEvent.recurringEventId
              ? `Select the primary calendar you'd like the event series to move to:`
              : `Select the primary calendar you'd like the event to move to:`}
          </Typography>
          <Box sx={{ width: '100%' }}>
            <SharedAutocomplete
              autoFocus
              sources={['users']}
              autocompleteValue={autoCompleteValue}
              setAutoCompleteValue={setAutoCompleteValue}
            />
          </Box>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        title: selectedEvent.recurringEventId ? 'Move All' : 'Move',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending || !autoCompleteValue,
      }}
    />
  );
}
